import React, { useState, useRef, useEffect, useCallback } from "react";
import "../Style/Servicessection.css";

import uiuxImg from "../assets/uiux23.gif";
import webImg from "../assets/Web.gif";
import appImg from "../assets/App.gif";
import saasImg from "../assets/SAAS.gif";
import mktImg from "../assets/d2.png";
import graphicImg from "../assets/Graphic designing.gif";
import videoImg from "../assets/VIDEO.png";

const services = [
  {
    code: "UX",
    label: "UI/UX Design",
    image: uiuxImg,
    desc1:
      "We design experiences that feel simple, intuitive, and human — while solving real business problems.",
    tags: [
      "User Research",
      "Wireframes",
      "UI Design",
      "Prototyping",
      "Usability Testing",
    ],
  },
  {
    code: "WEB",
    label: "Web Development",
    image: webImg,
    desc1:
      "Fast, responsive websites built for performance, security, and scalability.",
    tags: [
      "Responsive Design",
      "Clean Code",
      "SEO-Friendly",
      "High Performance",
    ],
  },
  {
    code: "APP",
    label: "App Development",
    image: appImg,
    desc1: "Mobile apps designed for smooth experiences and long-term growth.",
    tags: ["Android", "iOS", "Cross Platform", "API Integration"],
  },
  {
    code: "SAAS",
    label: "SaaS Development",
    image: saasImg,
    desc1:
      "End-to-end SaaS products built for scale — from architecture to launch and beyond.",
    tags: ["Multi-Tenant", "Subscriptions", "Dashboards", "Auth & Billing"],
  },
  {
    code: "MKT",
    label: "Digital Marketing",
    image: mktImg,
    desc1:
      "Data-driven digital marketing strategies that grow your brand and bring qualified leads.",
    tags: ["SEO", "Google Ads", "Social Ads", "Analytics"],
  },
  {
    code: "GFX",
    label: "Graphic Design",
    image: graphicImg,
    desc1:
      "Visual designs that communicate clearly and strengthen brand identity across every touchpoint.",
    tags: ["Branding", "Logo Design", "Social Media", "Illustrations"],
  },
  {
    code: "VID",
    label: "Video Editing & Production",
    image: videoImg,
    desc1:
      "Professional video editing and production that tells your brand story with impact and clarity.",
    tags: ["Reels / Shorts", "Brand Films", "Motion Graphics", "Color Grading"],
  },
];

const pad = (n) => String(n).padStart(2, "0");
const wrap = (i, len) => ((i % len) + len) % len;

const OurServices = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);
  const hoveredRef = useRef(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const goTo = useCallback((i) => {
    setActiveIndex(wrap(i, services.length));
  }, []);

  // Continuous circular autoplay — pauses on hover, respects reduced motion
  useEffect(() => {
    if (!visible) return;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce) return;
    const id = setInterval(() => {
      if (hoveredRef.current) return;
      setActiveIndex((prev) => wrap(prev + 1, services.length));
    }, 4200);
    return () => clearInterval(id);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") goTo(activeIndex + 1);
      if (e.key === "ArrowLeft") goTo(activeIndex - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, activeIndex, goTo]);

  return (
    <section
      className={`rs-container ${visible ? "rs-visible" : ""}`}
      ref={sectionRef}
    >
      <div className="rs-titleblock">
        <span className="rs-eyebrow">Capability Registry</span>
        <h2 className="rs-title">Our Services</h2>
        <span className="rs-title-count">
          {pad(services.length)} disciplines on file
        </span>
      </div>

      <div
        className="rs-stage-wrap"
        onMouseEnter={() => {
          hoveredRef.current = true;
        }}
        onMouseLeave={() => {
          hoveredRef.current = false;
        }}
      >
        <button
          type="button"
          className="rs-nav-btn rs-nav-prev"
          onClick={() => goTo(activeIndex - 1)}
          aria-label="Previous service"
        >
          ‹
        </button>

        <div className="rs-stage">
          {services.map((svc, index) => {
            let offset = index - activeIndex;
            const len = services.length;
            if (offset > len / 2) offset -= len;
            if (offset < -len / 2) offset += len;
            const abs = Math.abs(offset);
            if (abs > 2) return null;

            const style = {
              transform: `
    translateX(-50%)
    translateX(${offset * 38}%)
    translateZ(${-abs * 60}px)
    rotateY(${offset * -38}deg)
    scale(${offset === 0 ? 1 : 0.82})
  `,
              zIndex: 100 - abs,
              opacity: offset === 0 ? 1 : 0.75,
            };

            return (
              <div
                className={`rs-panel ${offset === 0 ? "rs-panel--active" : ""} ${offset < 0 ? "rs-panel--left" : ""} ${offset > 0 ? "rs-panel--right" : ""}`}
                style={style}
                key={svc.code}
                onClick={() => offset !== 0 && goTo(index)}
              >
                <div className="rs-panel-inner">
                  <div className="rs-panel-top">
                    <div className="rs-panel-eyebrow">
                      <span className="rs-panel-code">{svc.code}</span>
                      <span className="rs-panel-rule" />
                      <span className="rs-panel-index">{pad(index + 1)}</span>
                    </div>

                    <h3 className="rs-panel-title">{svc.label}</h3>

                    {offset === 0 && (
                      <>
                        <p className="rs-panel-desc">{svc.desc1}</p>
                        <div className="rs-panel-chips">
                          {svc.tags.map((tag) => (
                            <span className="rs-chip" key={tag}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="rs-panel-media">
                    <img src={svc.image} alt={svc.label} loading="lazy" />
                    <div className="rs-media-veil" />
                    <span className="rs-seal">
                      <span className="rs-seal-code">{svc.code}</span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="rs-nav-btn rs-nav-next"
          onClick={() => goTo(activeIndex + 1)}
          aria-label="Next service"
        >
          ›
        </button>
      </div>

      <div className="rs-index-strip">
        {services.map((svc, i) => (
          <button
            type="button"
            key={svc.code}
            className={`rs-index-chip ${activeIndex === i ? "rs-index-chip--active" : ""}`}
            onClick={() => goTo(i)}
          >
            <span className="rs-index-code">{svc.code}</span>
            <span className="rs-index-num">{pad(i + 1)}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

export default OurServices;